/* eslint-disable @typescript-eslint/no-explicit-any */
import type { FunctionComponent } from 'react';

import { getMemberId } from '@trello/authentication';
import { getMembership } from '@trello/business-logic/membership';
import { Entitlements } from '@trello/entitlements';
import { intl } from '@trello/i18n';
import { getOrganizationBillingUrl } from '@trello/urls';

import { WorkspaceGuestList } from 'app/src/components/WorkspaceGuestList';
import { LazyWorkspaceJoinRequestList } from 'app/src/components/WorkspaceJoinRequestList';
import { WorkspaceMembersList } from 'app/src/components/WorkspaceMembersList';
import { type MemberTypeFilter } from 'app/src/components/WorkspacePages/WorkspaceMembersPage';
import { useWorkspaceMembersListViewFragment } from './WorkspaceMembersListViewFragment.generated';

export interface WorkspaceMembersListViewProps {
  workspaceId: string;
  isAdmin: boolean;
  memberTypeFilter: MemberTypeFilter;
}

function getMemberType(member: any, memberships: any[]) {
  if (member.memberType === 'ghost') {
    return 'pending';
  }

  const membership = getMembership(memberships, member.id);
  if (membership?.deactivated) {
    return 'deactivated';
  }
  return membership?.memberType ?? '';
}

function convertToModel(
  member: any,
  isAdmin: boolean,
  hasPaidProduct: boolean,
  memberships: any[],
  isCollaborator: boolean,
) {
  const memberType = isCollaborator
    ? 'public'
    : getMemberType(member, memberships);
  const props = {
    id: member.id,
    isPending: memberType === 'pending',
    isDeactivated: memberType === 'deactivated' || member.activityBlocked,
    fullName: member.fullName,
    username: member.username,
    isOrgMember: memberType !== 'public',
    allowTypeChange:
      isAdmin &&
      memberType !== 'deactivated' &&
      memberType !== 'unconfirmed' &&
      memberType !== 'pending',
    orgMemberType: intl.formatMessage(
      {
        id: `member types.${memberType}`,
        description: 'The type of the member in the organization',
        defaultMessage: '{memberType}',
      },
      { memberType },
    ),
    allowReactivate: memberType === 'deactivated' && !member.activityBlocked,
    isMe: member.id === getMemberId(),
    orgIsBC: hasPaidProduct,
    ownsOrg: isAdmin,
    avatarUrl: member.avatarUrl,
    initials: member.initials,
    isOrganizationAdmin: memberType === 'admin',
    idEnterprise: member.idEnterprise,
  };

  return props;
}

export const WorkspaceMembersListView: FunctionComponent<
  WorkspaceMembersListViewProps
> = ({ workspaceId, isAdmin, memberTypeFilter }) => {
  const { data: organization } = useWorkspaceMembersListViewFragment({
    from: {
      id: workspaceId,
    },
  });

  if (!organization) {
    return null;
  }

  const hasPaidProduct = !Entitlements.isFree(organization.offering);
  const orgType = Entitlements.isStandard(organization.offering)
    ? 'standard'
    : Entitlements.isPremium(organization.offering) // Is this the correct way to check for bc?
      ? 'bc'
      : Entitlements.isEnterprise(organization.offering)
        ? 'enterprise'
        : 'free';

  const orgEnterpriseName = organization.enterprise?.displayName;
  const orgIdEnterprise = organization.idEnterprise?.toString();
  const orgMemberModels = organization.members.map((m) => {
    return convertToModel(
      m,
      isAdmin,
      hasPaidProduct,
      organization.memberships,
      false,
    );
  });

  const orgCollabModels = organization.collaborators.map((m) => {
    return convertToModel(
      m,
      isAdmin,
      hasPaidProduct,
      organization.memberships,
      true,
    );
  });

  if (memberTypeFilter === 'guest') {
    return (
      <WorkspaceGuestList
        memberItems={orgCollabModels}
        memberTypeFilter={memberTypeFilter}
        orgEnterpriseName={orgEnterpriseName}
        orgIdEnterprise={orgIdEnterprise}
        orgIsBC={hasPaidProduct}
        orgType={orgType}
        workspaceId={workspaceId}
      />
    );
  }

  if (memberTypeFilter === 'request') {
    return (
      <LazyWorkspaceJoinRequestList
        memberTypeFilter={memberTypeFilter}
        orgEnterpriseName={orgEnterpriseName}
        orgId={workspaceId}
        orgIdEnterprise={orgIdEnterprise}
        orgMemberLimit={organization.limits.orgs.totalMembersPerOrg.disableAt}
        orgType={orgType}
      />
    );
  }

  return (
    <WorkspaceMembersList
      memberItems={orgMemberModels as any[]}
      memberTypeFilter={memberTypeFilter}
      orgBillingUrl={getOrganizationBillingUrl(organization.id)}
      orgEnterpriseName={organization?.enterprise?.displayName}
      orgIdEnterprise={organization.idEnterprise?.toString()}
      workspaceId={workspaceId}
    />
  );
};
